import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import axios, { CancelTokenSource } from 'axios';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';

import InfoTooltip from './InfoTooltip';
import {
  CHANNEL_STYLES,
  ChannelFill,
  DEFAULT_CHANNEL_STYLE,
  MIX_CHANNEL_DEFINITIONS,
  OTHER_CHANNEL_NAME,
  PENDING_COLOR,
  stackIndex,
} from '../constants/channelMix';
import {
  ChannelMixRow,
  ChannelStageCounts,
  FUNNEL_STAGES,
  FunnelStage,
  StageCounts,
  channelMixShare,
  computeChannelMix,
  formatPercent,
  stageConversion,
} from '../models/FunnelModel';
import { fetchFunnelByChannel } from '../services/AcquisitionFunnelCRUD';

// Alto suficiente para las cuatro barras y una leyenda de ~20 canales.
const CHART_HEIGHT_PX = 360;
const SEGMENT_GAP_PX = 2;
const SURFACE_COLOR = '#ffffff';
const GRID_COLOR = '#ebedf3';
const SWATCH_SIZE_PX = 10;
const INFO_TOOLTIP_WIDTH_PX = 320;
const PERCENT_SCALE = 100;
const NO_CONVERSION = '—';
const PATTERN_TILE_PX = 8;
const HATCH_LINE_PX = 2;
const DOT_RADIUS_PX = 1.2;

const STAGE_I18N_KEY: Record<FunnelStage, string> = {
  sessions: 'marketingDashboard.stageSessions',
  leads: 'marketingDashboard.stageLeads',
  trials: 'marketingDashboard.stageTrials',
  subscriptions: 'marketingDashboard.stageSubscribers',
};

// Una franja de la barra o una fila de la tabla: un canal, las visitas sin
// canal o la parte aún sin repartir.
type Segment = {
  label: string;
  color: string;
  fill: ChannelFill;
  counts: StageCounts;
};

function channelSegment({ name, counts }: ChannelMixRow): Segment {
  const style =
    CHANNEL_STYLES.find((s) => s.name === name) ?? DEFAULT_CHANNEL_STYLE;
  return { label: name, color: style.color, fill: style.fill, counts };
}

function hasData({ counts }: Segment): boolean {
  return FUNNEL_STAGES.some((stage) => counts[stage] > 0);
}

// Cada etapa ordena por su cantidad o, salvo Sesiones, por su conversión desde
// la anterior.
type SortBy = 'count' | 'conversion';

type TableSort = {
  stage: FunnelStage;
  by: SortBy;
  descending: boolean;
};

// Sesiones abre el embudo: no tiene conversión.
function hasConversion(stage: FunnelStage): boolean {
  return stage !== FUNNEL_STAGES[0];
}

function sortValue({ counts }: Segment, { stage, by }: TableSort) {
  return by === 'count' ? counts[stage] : stageConversion(counts, stage);
}

type Props = {
  since: string;
  until: string;
  storeId: number | null;
  // Embudo sin filtro de canal con estos parámetros: de él sale lo que queda
  // sin repartir.
  total: StageCounts;
  // Mientras carga el embudo el reparto espera, para no competir con él.
  mainLoading: boolean;
  // Recibe los canales cuando han llegado todos, y null al empezar otra tanda.
  onCalculated: (channels: ChannelStageCounts[] | null) => void;
};

// Textura del color de la superficie sobre el color del canal: rayas
// diagonales (los tramos de las esquinas cierran la raya al repetirse la
// tesela) o puntos al tresbolillo.
function fillPattern(color: string, fill: ChannelFill) {
  if (fill === 'solid') return color;
  const tile = document.createElement('canvas');
  tile.width = PATTERN_TILE_PX;
  tile.height = PATTERN_TILE_PX;
  const ctx = tile.getContext('2d');
  if (!ctx) return color;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, PATTERN_TILE_PX, PATTERN_TILE_PX);
  if (fill === 'hatched') {
    ctx.strokeStyle = SURFACE_COLOR;
    ctx.lineWidth = HATCH_LINE_PX;
    ctx.beginPath();
    ctx.moveTo(0, PATTERN_TILE_PX);
    ctx.lineTo(PATTERN_TILE_PX, 0);
    ctx.moveTo(-1, 1);
    ctx.lineTo(1, -1);
    ctx.moveTo(PATTERN_TILE_PX - 1, PATTERN_TILE_PX + 1);
    ctx.lineTo(PATTERN_TILE_PX + 1, PATTERN_TILE_PX - 1);
    ctx.stroke();
  } else {
    ctx.fillStyle = SURFACE_COLOR;
    for (const offset of [PATTERN_TILE_PX / 4, (PATTERN_TILE_PX * 3) / 4]) {
      ctx.beginPath();
      ctx.arc(offset, offset, DOT_RADIUS_PX, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
  return ctx.createPattern(tile, 'repeat') ?? color;
}

// La misma textura en CSS, para la muestra de la tabla.
function swatchBackground({ color, fill }: Segment): string {
  if (fill === 'hatched') {
    return `repeating-linear-gradient(-45deg, ${color} 0 3px, ${SURFACE_COLOR} 3px 4px)`;
  }
  if (fill === 'dotted') {
    return `radial-gradient(circle, ${SURFACE_COLOR} 1px, transparent 1.3px) 0 0 / 4px 4px, ${color}`;
  }
  return color;
}

// Reparto de cada etapa del embudo entre todos los canales. Cada canal se pide
// por separado con su filtro, así que solo se pinta con "Todos". La barra y la
// tabla se van rellenando a medida que llegan los canales.
const ChannelMixChart: FC<Props> = ({
  since,
  until,
  storeId,
  total,
  mainLoading,
  onCalculated,
}) => {
  const intl = useIntl();
  const key = [since, until, storeId].join('|');
  const [arrived, setArrived] = useState<ChannelStageCounts[]>([]);
  // Parámetros de la tanda lanzada. Distintos de los actuales = esperando a
  // que termine el embudo con los nuevos.
  const [jobKey, setJobKey] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(true);
  const [tableSort, setTableSort] = useState<TableSort>({
    stage: 'sessions',
    by: 'count',
    descending: true,
  });
  // Al paginar sesiones el embudo recarga con los mismos parámetros y la tanda
  // sigue; si cambian, se cancela.
  const jobRef = useRef<{ key: string; source: CancelTokenSource } | null>(
    null
  );

  useEffect(() => {
    if (jobRef.current?.key === key) return;
    jobRef.current?.source.cancel();
    jobRef.current = null;
    setArrived([]);
    onCalculated(null);
    if (mainLoading) return;

    const source = axios.CancelToken.source();
    const isCancelled = () => source.token.reason !== undefined;
    jobRef.current = { key, source };
    setJobKey(key);
    const received: ChannelStageCounts[] = [];
    fetchFunnelByChannel(
      { since, until, store_id: storeId },
      MIX_CHANNEL_DEFINITIONS,
      source.token,
      (channel) => {
        if (isCancelled()) return;
        received.push(channel);
        setArrived([...received]);
      }
    )
      .then(() => {
        if (!isCancelled()) onCalculated(received);
      })
      // Solo rechaza al cancelar, y entonces ya manda otra tanda o ninguna.
      .catch(() => {});
  }, [key, since, until, storeId, mainLoading, onCalculated]);

  useEffect(() => () => jobRef.current?.source.cancel(), []);

  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `marketingDashboard.${id}` }, values);

  const waiting = jobKey !== key;
  const pendingCount = waiting
    ? MIX_CHANNEL_DEFINITIONS.length
    : MIX_CHANNEL_DEFINITIONS.length - arrived.length;

  const header = (
    <div className="d-flex align-items-center gap-3 mb-3">
      <span className="fs-6 fw-bold">{t('channelMixTitle')}</span>
      <InfoTooltip text={t('channelMixInfo')} widthPx={INFO_TOOLTIP_WIDTH_PX} />
      {pendingCount > 0 && (
        <>
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-hidden="true"
          />
          <span className="text-muted fs-7">
            {t('channelMixProgress', {
              done: MIX_CHANNEL_DEFINITIONS.length - pendingCount,
              total: MIX_CHANNEL_DEFINITIONS.length,
            })}
          </span>
        </>
      )}
    </div>
  );

  // Hasta que el embudo termina, `total` aún es el de los parámetros previos.
  if (waiting) {
    return <div className="mt-10">{header}</div>;
  }

  const mix = computeChannelMix(arrived, total);
  const share = (count: number, stage: FunnelStage) =>
    channelMixShare(mix, count, stage);
  const isPending = pendingCount > 0;
  // Lo que no reclama ningún canal llegado: mientras faltan canales es lo aún
  // sin repartir; al acabar, Otro.
  const pendingSegment: Segment = {
    label: t('channelMixPending'),
    color: PENDING_COLOR,
    fill: 'solid',
    counts: mix.remainder,
  };
  const channelSegments = [
    ...mix.channels.map(channelSegment),
    ...(isPending
      ? []
      : [channelSegment({ name: OTHER_CHANNEL_NAME, counts: mix.remainder })]),
  ];
  // Cada canal es su propia franja, en su sitio fijo de la barra aunque llegue
  // en otro orden.
  const segments: Segment[] = [
    ...[...channelSegments].sort(
      (a, b) => stackIndex(a.label) - stackIndex(b.label)
    ),
    ...(isPending ? [pendingSegment] : []),
  ];

  const data: ChartData<'bar'> = {
    labels: FUNNEL_STAGES.map((stage) =>
      intl.formatMessage({ id: STAGE_I18N_KEY[stage] })
    ),
    datasets: segments.map((segment) => ({
      label: segment.label,
      data: FUNNEL_STAGES.map(
        (stage) => share(segment.counts[stage], stage) * PERCENT_SCALE
      ),
      backgroundColor: fillPattern(segment.color, segment.fill),
      borderColor: SURFACE_COLOR,
      borderWidth: { right: SEGMENT_GAP_PX },
      borderSkipped: false,
    })),
  };

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    // Sin animación: con la barra rellenándose canal a canal, cada llegada
    // la volvería a animar desde cero.
    animation: false,
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: PERCENT_SCALE,
        ticks: { callback: (value) => `${value}%` },
        grid: { color: GRID_COLOR },
      },
      y: { stacked: true, grid: { display: false } },
    },
    plugins: {
      legend: {
        position: 'bottom',
        // En un reparto al 100 % ocultar una franja descuadraría la barra.
        onClick: () => undefined,
        // Todos los canales con algo que contar, como en la tabla.
        labels: {
          filter: (item) =>
            item.datasetIndex !== undefined &&
            hasData(segments[item.datasetIndex]),
        },
      },
      tooltip: {
        callbacks: {
          label: (item) => {
            const segment = segments[item.datasetIndex];
            const stage = FUNNEL_STAGES[item.dataIndex];
            const count = segment.counts[stage];
            return `${segment.label}: ${count} (${formatPercent(share(count, stage))})`;
          },
          afterLabel: (item) =>
            segments[item.datasetIndex] === pendingSegment
              ? t('channelMixPendingHint', { count: pendingCount })
              : '',
        },
      },
    },
  };

  // Tabla de detalle: cada canal con algo que contar, ordenado por la columna
  // elegida, y lo aún sin repartir al final. Los canales sin conversión (con
  // la etapa anterior a cero) van detrás en los dos sentidos.
  const sortSign = tableSort.descending ? -1 : 1;
  function compareRows(a: Segment, b: Segment): number {
    const valueA = sortValue(a, tableSort);
    const valueB = sortValue(b, tableSort);
    if (valueA === null || valueB === null) {
      return Number(valueA === null) - Number(valueB === null);
    }
    return sortSign * (valueA - valueB);
  }
  const tableRows: Segment[] = [
    ...channelSegments.filter(hasData).sort(compareRows),
    ...(isPending ? [pendingSegment] : []),
  ];

  // Clic en otra columna: ordena por ella de mayor a menor; en la misma,
  // invierte el sentido.
  function onSortClick(stage: FunnelStage, by: SortBy) {
    setTableSort((current) =>
      current.stage === stage && current.by === by
        ? { ...current, descending: !current.descending }
        : { stage, by, descending: true }
    );
  }

  function isSortedBy(stage: FunnelStage, by: SortBy): boolean {
    return tableSort.stage === stage && tableSort.by === by;
  }

  function sortIcon(stage: FunnelStage, by: SortBy): string {
    if (!isSortedBy(stage, by)) return 'fa-sort text-muted';
    return tableSort.descending ? 'fa-sort-down' : 'fa-sort-up';
  }

  return (
    <div className="mt-10">
      {header}

      {mix.failedNames.length > 0 && (
        <div className="text-warning fs-7 mb-3">
          {t('channelMixFailed', { channels: mix.failedNames.join(', ') })}
        </div>
      )}

      <div style={{ height: `${CHART_HEIGHT_PX}px` }}>
        <Bar data={data} options={options} />
      </div>

      <button
        type="button"
        className="btn btn-link btn-sm px-0 mt-3"
        onClick={() => setShowTable(!showTable)}
        aria-expanded={showTable}
      >
        {t(showTable ? 'channelMixHideTable' : 'channelMixShowTable')}
        <i className={`fa fa-chevron-${showTable ? 'up' : 'down'} ms-2`} />
      </button>

      {showTable && (
        <div className="card sessions-card-inset overflow-hidden mt-3">
          <div className="card-body p-0">
            <table className="table table-striped table-row-bordered gs-2 mb-0">
              <thead className="fw-bolder">
                <tr>
                  <th>{t('channelMixChannelHeader')}</th>
                  {FUNNEL_STAGES.map((stage) => {
                    const ariaSort = tableSort.descending
                      ? 'descending'
                      : 'ascending';
                    return (
                      <th
                        key={stage}
                        className="text-end"
                        aria-sort={
                          tableSort.stage === stage ? ariaSort : 'none'
                        }
                      >
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 fw-bolder text-reset"
                          onClick={() => onSortClick(stage, 'count')}
                        >
                          {intl.formatMessage({ id: STAGE_I18N_KEY[stage] })}
                          <i
                            className={`fa ${sortIcon(stage, 'count')} ms-2`}
                          />
                        </button>
                        {hasConversion(stage) && (
                          <button
                            type="button"
                            className={`d-block ms-auto border-0 bg-transparent p-0 fs-7 fw-normal ${
                              isSortedBy(stage, 'conversion')
                                ? 'text-reset'
                                : 'text-muted'
                            }`}
                            onClick={() => onSortClick(stage, 'conversion')}
                          >
                            {t('channelMixConversionHeader')}
                            <i
                              className={`fa ${sortIcon(stage, 'conversion')} ms-2`}
                            />
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.label}>
                    <td>
                      <span
                        className="d-inline-block rounded-1 me-2"
                        style={{
                          width: `${SWATCH_SIZE_PX}px`,
                          height: `${SWATCH_SIZE_PX}px`,
                          background: swatchBackground(row),
                        }}
                        aria-hidden="true"
                      />
                      {row.label}
                    </td>
                    {FUNNEL_STAGES.map((stage) => (
                      <td key={stage} className="text-end">
                        {row.counts[stage]}
                        {/* Lo aún sin repartir mezcla los canales que faltan:
                            su conversión no es la de ninguno. */}
                        {hasConversion(stage) && row !== pendingSegment && (
                          <span className="text-muted">
                            {` (${
                              formatPercent(
                                stageConversion(row.counts, stage)
                              ) || NO_CONVERSION
                            })`}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelMixChart;
